import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip, notification } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'

import { apiBase, requestHeader } from '../../helpers/restRequest'

const buildExportParams = (filters, itemId) => {
  const params = new URLSearchParams()
  params.set('paid_from', filters.paidFrom)
  params.set('paid_to', filters.paidTo)
  if (filters.query) params.set('query', filters.query)
  if (itemId) params.set('item_id', itemId)
  if (filters.provinceId) params.set('province_id', filters.provinceId)
  if (filters.districtId) params.set('district_id', filters.districtId)
  if (filters.requestItemsOnly) params.set('request_items_only', 'true')
  if (filters.sort) params.set('sort', filters.sort)
  filters.orderStates.forEach((state) => params.append('order_states[]', state))
  return params.toString()
}

const readError = async (response) => {
  try {
    const body = await response.json()
    return body.error_description || body.error || response.statusText
  } catch {
    return response.statusText
  }
}

const ExportButton = ({ filters, itemId, disabled }) => {
  const { t } = useTranslation('lineItems')
  const [loading, setLoading] = useState(false)
  const hasDateRange = Boolean(filters.paidFrom && filters.paidTo)

  const handleExport = async () => {
    setLoading(true)
    try {
      const headers = await requestHeader({ Accept: 'application/json' })
      const response = await fetch(`${apiBase}/line_items/exports?${buildExportParams(filters, itemId)}`, { headers })
      if (response.status === 202) {
        notification.success({
          message: t('export.sentTitle'),
          description: t('export.sentDescription')
        })
      } else {
        notification.error({
          message: t('export.failedTitle'),
          description: await readError(response)
        })
      }
    } catch (error) {
      notification.error({ message: t('export.failedTitle'), description: error.message })
    } finally {
      setLoading(false)
    }
  }

  const button = (
    <Button icon={<DownloadOutlined />} loading={loading} disabled={disabled || !hasDateRange} onClick={handleExport}>
      {t('export.button')}
    </Button>
  )

  if (hasDateRange) return button

  return (
    <Tooltip title={t('export.needDateRange')}>
      <span>{button}</span>
    </Tooltip>
  )
}

ExportButton.propTypes = {
  filters: PropTypes.shape({
    query: PropTypes.string,
    provinceId: PropTypes.string,
    districtId: PropTypes.string,
    orderStates: PropTypes.arrayOf(PropTypes.string).isRequired,
    requestItemsOnly: PropTypes.bool,
    paidFrom: PropTypes.string,
    paidTo: PropTypes.string,
    sort: PropTypes.string
  }).isRequired,
  itemId: PropTypes.string,
  disabled: PropTypes.bool
}

ExportButton.defaultProps = {
  itemId: undefined,
  disabled: false
}

export default ExportButton
